import { useCallback, useMemo } from 'react';
import type {
	ContextTokenType,
	GetContextToken,
} from '@atlassian/jira-forge-ui-types/src/common/types/context-token.tsx';
import type { ExtensionData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import type { Extension } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import { createFetchForgeCmdbContextToken } from './controllers/cmdb/index.tsx';
import { fetchForgeCommonContextToken } from './controllers/common/index.tsx';
import { fetchForgeServiceDeskContextToken } from './controllers/servicedesk/index.tsx';
import type {
	ContextTokenData,
	FetchForgeContextTokenFunction,
	UseContextTokenOptions,
} from './types.tsx';

export class ContextTokenCache {
	private static instance: ContextTokenCache;

	private contextTokenCacheMap: Map<string, ContextTokenData>;

	static getInstance() {
		if (!ContextTokenCache.instance) {
			ContextTokenCache.instance = new ContextTokenCache();
		}
		return ContextTokenCache.instance;
	}

	constructor() {
		this.contextTokenCacheMap = new Map<string, ContextTokenData>();
	}

	setValue(key: string, value: ContextTokenData): void {
		this.contextTokenCacheMap.set(key, value);
	}

	getValue(key: string): ContextTokenData | undefined {
		return this.contextTokenCacheMap.get(key);
	}

	del(key: string): void {
		this.contextTokenCacheMap.delete(key);
	}
}

function fetchContextTokenFromCache(
	extension: Extension,
	extensionData: ExtensionData,
	localId: string | undefined,
) {
	const cacheKey = JSON.stringify({ id: localId || extension.id, data: extensionData });
	return { cachedToken: ContextTokenCache.getInstance().getValue(cacheKey), cacheKey };
}

async function fetchContextTokenFromServer(
	extension: Extension,
	extensionData: ExtensionData,
	localId: string | undefined,
	fetchForgeContextTokenFn: FetchForgeContextTokenFunction,
	cacheKey: string,
) {
	const serverResponse = await fetchForgeContextTokenFn({ extension, extensionData, localId });
	const tokenData: ContextTokenData = { ...serverResponse, issuedAt: Date.now() };
	ContextTokenCache.getInstance().setValue(cacheKey, tokenData);
	return tokenData;
}

export const TOKEN_LIFETIME_EXTRA_BUFFER_PERCENT = 0.01; // 10%

async function fetchContextToken(
	extension: Extension,
	extensionData: ExtensionData,
	localId: string | undefined,
	opts: UseContextTokenOptions,
) {
	const { fetchForgeContextTokenFn, useClientRelativeExpirationTime } = opts;
	const { cachedToken, cacheKey } = fetchContextTokenFromCache(extension, extensionData, localId);

	if (cachedToken) {
		// 1. Determining token lifetime using client-relative method
		if (useClientRelativeExpirationTime && typeof cachedToken.expiresIn === 'number') {
			if (
				typeof cachedToken.expiresIn === 'number' &&
				Date.now() -
					cachedToken.issuedAt +
					Math.ceil(cachedToken.expiresIn * TOKEN_LIFETIME_EXTRA_BUFFER_PERCENT) >
					cachedToken.expiresIn
			) {
				const tokenData = await fetchContextTokenFromServer(
					extension,
					extensionData,
					localId,
					fetchForgeContextTokenFn,
					cacheKey,
				);
				return tokenData.token;
			}
		}

		// 2. Determining token lifetime using server
		// (potentially buggy! see https://gist.github.com/timvisee/fcda9bbdff88d45cc9061606b4b923ca)
		if (typeof cachedToken.expiresAt === 'number' && Date.now() > cachedToken.expiresAt) {
			const tokenData = await fetchContextTokenFromServer(
				extension,
				extensionData,
				localId,
				fetchForgeContextTokenFn,
				cacheKey,
			);
			return tokenData.token;
		}
		return cachedToken.token;
	}

	const tokenData = await fetchContextTokenFromServer(
		extension,
		extensionData,
		localId,
		fetchForgeContextTokenFn,
		cacheKey,
	);
	return tokenData.token;
}

const USE_COMMON_CONTEXT_TOKEN_OPTS: UseContextTokenOptions = {
	fetchForgeContextTokenFn: fetchForgeCommonContextToken,
	useClientRelativeExpirationTime: true,
};

export const useContextToken = (
	extension: Extension,
	extensionData: ExtensionData,
	localId: string | undefined,
	opts = USE_COMMON_CONTEXT_TOKEN_OPTS,
) => {
	const getContextToken: GetContextToken = useCallback(
		() => fetchContextToken(extension, extensionData, localId, opts),
		[extension, extensionData, localId, opts],
	);

	return useMemo(
		() => ({
			getContextToken,
		}),
		[getContextToken],
	);
};

export const useServiceDeskContextToken = (extension: Extension, extensionData: ExtensionData) =>
	useContextToken(extension, extensionData, undefined, {
		fetchForgeContextTokenFn: fetchForgeServiceDeskContextToken,
		useClientRelativeExpirationTime: false,
	});

export const useCmdbContextToken = (extension: Extension, extensionData: ExtensionData) => {
	const { workspaceId } = useWorkspaceContext();

	return useContextToken(extension, extensionData, undefined, {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		fetchForgeContextTokenFn: createFetchForgeCmdbContextToken(workspaceId!),
		useClientRelativeExpirationTime: false,
	});
};

export const useGetContextTokenForType = (
	type: ContextTokenType | undefined,
	extension: Extension,
	extensionData: ExtensionData,
	localId: string | undefined,
) => {
	const jiraContextToken = useContextToken(extension, extensionData, localId);
	const jsmContextToken = useServiceDeskContextToken(extension, extensionData);
	const cmdbContextToken = useCmdbContextToken(extension, extensionData);

	if (!type) {
		return undefined;
	}

	switch (type) {
		case 'cmdb':
			return cmdbContextToken.getContextToken;
		case 'jsm':
			return jsmContextToken.getContextToken;
		case 'jira':
		default:
			return jiraContextToken.getContextToken;
	}
};
